/**
 * VectorForge "Buy Now"
 *
 * Progressive enhancement over a plain <form action="/cart/add"> that already
 * works without JavaScript (Shopify honours `return_to=/checkout`).
 *
 * With JS we:
 *   1. look at the current cart (/cart.js)
 *      - if it already holds items (e.g. several licenses added with "Add to cart"),
 *        we never clear it; "Buy now" then means "check out with what I have",
 *        adding this variant only if it isn't in the cart yet
 *      - if the cart is empty, we optionally clear it (no-op) and add one unit
 *   2. send the shopper straight to /checkout
 *
 * The old behaviour of always clearing the cart first is gone: it would have
 * silently thrown away licenses a shopper had just added.
 */
(function () {
  if (window.__vfBuyNowBound) return;
  window.__vfBuyNowBound = true;

  const SELECTOR = 'form[data-vf-buy-now]';

  function setBusy(form, busy, message) {
    const button = form.querySelector('[data-vf-buy-now-button]');
    const error = form.parentElement && form.parentElement.querySelector('[data-vf-buy-now-error]');
    if (button) {
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      button.disabled = !!busy;
    }
    if (error) error.textContent = message || '';
  }

  async function postJSON(url, body, method) {
    const response = await fetch(url, {
      method: method || 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });
    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      /* ignore */
    }
    if (!response.ok) {
      const message = (data && (data.description || data.message)) || 'Something went wrong. Please try again.';
      throw new Error(message);
    }
    return data;
  }

  async function handleSubmit(event) {
    const form = event.target.closest(SELECTOR);
    if (!form) return;
    event.preventDefault();

    const variantId = form.querySelector('input[name="id"]').value;
    const clearFirst = form.dataset.clearCart === 'true';
    const checkoutUrl = form.dataset.checkoutUrl || '/checkout';
    const routes = {
      clear: form.dataset.cartClearUrl || '/cart/clear.js',
      add: form.dataset.cartAddUrl || '/cart/add.js',
      cart: form.dataset.cartUrl || '/cart.js',
    };

    setBusy(form, true);

    try {
      let cart = null;
      try {
        cart = await postJSON(routes.cart, null, 'GET');
      } catch (_) {
        /* if the lookup fails we simply behave as if the cart were empty */
      }
      const items = (cart && Array.isArray(cart.items) && cart.items) || [];
      const cartHasItems = items.length > 0;
      const variantInCart = items.some((item) => Number(item.variant_id) === Number(variantId));

      if (clearFirst && !cartHasItems) {
        // Nothing to lose; keep the historical "start from a clean cart" step.
        await postJSON(routes.clear);
      }
      if (!variantInCart) {
        await postJSON(routes.add, { items: [{ id: Number(variantId), quantity: 1 }] });
      }

      // Let analytics hooks (Shopify pixels, GA4 via theme app extensions) see the event.
      document.dispatchEvent(
        new CustomEvent('vf:buy-now', {
          detail: { variantId: Number(variantId), added: !variantInCart, cartHadItems: cartHasItems },
          bubbles: true,
        })
      );

      window.location.assign(checkoutUrl);
    } catch (error) {
      setBusy(form, false, error.message);
      // Fall back to the native form POST (return_to=/checkout) on a second try.
      form.dataset.vfFallback = 'true';
    }
  }

  document.addEventListener('submit', function (event) {
    const form = event.target.closest(SELECTOR);
    if (!form) return;
    if (form.dataset.vfFallback === 'true') return; // let the browser submit normally
    handleSubmit(event);
  });
})();
